import React, {
	memo,
	useCallback,
	useState,
	useMemo,
	useLayoutEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { Stepper, CheckBox } from "uikit";
import { cloneDeep, isNumber } from "lodash";
import { useDebouncedCallback } from "use-debounce";

import { Schema } from "../../../../../../../../redux/constants/OrdersPage/order";
import {
	CustomerBonusUsageType,
	Discount,
} from "../../../../../../../../services/Order";
import { UseCreateFormReturn } from "../../../../../../../../hooks/useCreateForm";
import {
	useObjectEditor,
	useCheckFlagAccessControl,
	useCurrencyGlobalSettings,
} from "../../../../../../../../hooks";
import { DiscountUnit } from "../../../../../../../../redux/services/Order/getPhoneInfo";
import {
	StyledColumn,
	StyledP,
	StyledRow,
	StyledSpan,
	Text,
} from "../../../../../../../../components/common";
import { useOrderModalContext } from "../../../../../context";
import { TabAccessNames, TabKeys } from "../../constants/access";
import { Tab } from "../../types/Tab";

const DiscountPlaneBase: React.FC<DiscountPlaneBase.Props> = ({
	disabled,
	form,
	tab,
}) => {
	const { t } = useTranslation();
	const { isOwn, transfer, costPreviewRunning } = useOrderModalContext();
	const currencyGlobalSettings = useCurrencyGlobalSettings();
	const checkFlagAccessControl = useCheckFlagAccessControl(TabAccessNames);
	const defaultValue = useMemo<DiscountPlaneBase.Value>(
		() => ({
			activeDiscount: true,
			activeDiscountUsage: true,
			activeBonusUsage: false,
			typeBonusUsage: CustomerBonusUsageType.BonusBalance,
			activeBonusUsageLimit: false,
			maxAmountBonusUsageLimit: 0,
		}),
		[],
	);

	const [intervalValue, setIntervalValue] =
		useState<DiscountPlaneBase.Value>(defaultValue);
	const valueEditor = useObjectEditor(intervalValue, setIntervalValue);

	const activeDiscount = valueEditor.useGetter("activeDiscount");

	const typeBonusUsage = valueEditor.useGetter("typeBonusUsage");
	const setTypeBonusUsage = valueEditor.useSetter("typeBonusUsage");

	const activeDiscountUsage = valueEditor.useGetter("activeDiscountUsage");
	const setActiveDiscountUsage = valueEditor.useSetter("activeDiscountUsage");

	const activeBonusUsage = valueEditor.useGetter("activeBonusUsage");
	const setActiveBonusUsage = valueEditor.useSetter("activeBonusUsage");
	const activeBonusUsageLimit = valueEditor.useGetter(
		"activeBonusUsageLimit",
	);
	const setActiveBonusUsageLimit = valueEditor.useSetter(
		"activeBonusUsageLimit",
	);
	const maxAmountBonusUsageLimit = valueEditor.useGetter(
		"maxAmountBonusUsageLimit",
	);
	const setMaxAmountBonusUsageLimit = valueEditor.useSetter(
		"maxAmountBonusUsageLimit",
	);

	const [show, setShow] = useState(false);

	const customerId = useMemo(
		() => tab.state?.customerId || "",
		[tab.state?.customerId],
	);

	const isConterparty = useMemo(
		() => tab.form.counterparty && tab.form?.code,
		[tab.form?.counterparty, tab.form?.code],
	);

	const customerDiscountPlansFromTransfer = useMemo(() => {
		let mainPlan =
			transfer?.lastReceivedOrder?.cost?.settings?.customerDiscountPlans?.find(
				(item) => item?.type === "main",
			);

		let additionalPlan =
			transfer?.lastReceivedOrder?.cost?.settings?.customerDiscountPlans?.find(
				(item) => item?.type === "additional",
			);

		if (mainPlan) {
			mainPlan = {
				...mainPlan,
				...(mainPlan?.conciseSettings || {}),
			};
		}

		if (additionalPlan) {
			additionalPlan = {
				...additionalPlan,
				...(additionalPlan?.conciseSettings || {}),
			};
		}

		return { additionalPlan, mainPlan };
	}, [transfer]);

	const discount = useMemo(() => tab.form.discount, [tab.form.discount]);
	const discountCost = useMemo(
		() => tab.form.discountCost,
		[tab.form.discountCost],
	);
	const additionalPlan = useMemo(
		() =>
			tab.form.additionalPlan ||
			customerDiscountPlansFromTransfer.additionalPlan,
		[
			customerDiscountPlansFromTransfer.additionalPlan,
			tab.form.additionalPlan,
		],
	);
	const mainPlan = useMemo(
		() => tab.form?.mainPlan || customerDiscountPlansFromTransfer.mainPlan,
		[customerDiscountPlansFromTransfer.mainPlan, tab.form?.mainPlan],
	);

	const modifyDiscountToForm = useCallback(
		(value: DiscountPlaneBase.Value) => {
			const payload = {
				active: value.activeDiscount,
				bonusUsage: {
					active: value.activeBonusUsage,
					type: value.typeBonusUsage,
					limit: {
						active: value.activeBonusUsageLimit,
						maxAmount: value.maxAmountBonusUsageLimit,
					},
				},
				discountUsage: {
					active: value.activeDiscountUsage,
				},
			};
			return payload;
		},
		[],
	);

	useLayoutEffect(() => {
		if (discount) {
			const payload: DiscountPlaneBase.Value = {
				...defaultValue,
				activeDiscount: discount?.active || false,
				activeDiscountUsage: discount.discountUsage?.active || false,
				activeBonusUsage: discount.bonusUsage?.active || false,
				typeBonusUsage:
					discount.bonusUsage?.type ||
					CustomerBonusUsageType.BonusBalance,
				activeBonusUsageLimit:
					discount.bonusUsage?.limit?.active || false,
				maxAmountBonusUsageLimit:
					discount.bonusUsage?.limit?.maxAmount || 0,
			};

			setIntervalValue(payload);
		} else setIntervalValue(defaultValue);
	}, [defaultValue, discount]);

	const handleSave = useCallback(() => {
		form.setValue("orderSave", true);
	}, [form]);

	useLayoutEffect(() => {
		if (customerId || isConterparty) {
			if (additionalPlan) setShow(true);
			if (mainPlan) setShow(true);
			if (discount) setShow(true);
		}
	}, [
		additionalPlan,
		mainPlan,
		discount,
		discountCost,
		customerId,
		isConterparty,
	]);

	useLayoutEffect(() => {
		if (show && !discount) {
			const data = modifyDiscountToForm(cloneDeep(intervalValue));
			form.setValue("discount", data);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, discount]);

	const handelDiscount = useCallback(
		(value) => {
			setActiveDiscountUsage(value);

			const data = modifyDiscountToForm({
				...cloneDeep(intervalValue),
				activeDiscountUsage: value,
			});
			form.setValue("discount", data);
			handleSave();
		},
		[
			setActiveDiscountUsage,
			modifyDiscountToForm,
			intervalValue,
			form,
			handleSave,
		],
	);

	const handelActionBonusUsage = useCallback(
		(value) => {
			setActiveBonusUsage(value);
			const data = modifyDiscountToForm({
				...cloneDeep(intervalValue),
				activeBonusUsage: value,
			});
			form.setValue("discount", data);
			handleSave();
		},
		[
			setActiveBonusUsage,
			modifyDiscountToForm,
			intervalValue,
			form,
			handleSave,
		],
	);

	const handelActionBonusUsageLimit = useCallback(
		(value) => {
			setActiveBonusUsageLimit(value);
			const data = modifyDiscountToForm({
				...cloneDeep(intervalValue),
				activeBonusUsageLimit: value,
			});
			form.setValue("discount", data);
			handleSave();
		},
		[
			setActiveBonusUsageLimit,
			modifyDiscountToForm,
			intervalValue,
			form,
			handleSave,
		],
	);

	const handelBonusTripType = useCallback(() => {
		setTypeBonusUsage(CustomerBonusUsageType.BonusTrip);
		const data = modifyDiscountToForm({
			...cloneDeep(intervalValue),
			typeBonusUsage: CustomerBonusUsageType.BonusTrip,
			activeBonusUsage: true,
			activeBonusUsageLimit: false,
		});
		form.setValue("discount", data);
	}, [setTypeBonusUsage, modifyDiscountToForm, intervalValue, form]);

	const handelBonusBalanceType = useCallback(() => {
		setTypeBonusUsage(CustomerBonusUsageType.BonusBalance);
		const data = modifyDiscountToForm({
			...cloneDeep(intervalValue),
			typeBonusUsage: CustomerBonusUsageType.BonusBalance,
			activeBonusUsage: false,
		});
		form.setValue("discount", data);
	}, [setTypeBonusUsage, modifyDiscountToForm, intervalValue, form]);

	const handelMaxAmountBonusUsageLimit = useCallback(
		(value: Stepper.Value) => {
			if (isNumber(value)) {
				setMaxAmountBonusUsageLimit(value);
				const data = modifyDiscountToForm({
					...cloneDeep(intervalValue),
					maxAmountBonusUsageLimit: value,
				});
				form.setValue("discount", data);
				handleSave();
			}
		},
		[
			setMaxAmountBonusUsageLimit,
			modifyDiscountToForm,
			intervalValue,
			form,
			handleSave,
		],
	);

	useDebouncedCallback((value) => {
		handelMaxAmountBonusUsageLimit(value);
	}, 300);

	// Locked while the cost preview runs: answers of quick clicks came out of
	// order and the save took the preview that did not match the checkbox
	// (dev check G9, order №10).
	const disabledBonusCheckBox = useMemo(() => {
		if (disabled || costPreviewRunning) return true;
		return !activeDiscount;
	}, [disabled, costPreviewRunning, activeDiscount]);

	const disabledBonusLimitCheckBox = useMemo(() => {
		if (disabled || costPreviewRunning) return true;
		if (typeBonusUsage === CustomerBonusUsageType.BonusTrip) return true;
		return !activeBonusUsage;
	}, [disabled, costPreviewRunning, typeBonusUsage, activeBonusUsage]);

	const disabledBonusValue = useMemo(() => {
		if (disabled || costPreviewRunning) return true;
		return !activeBonusUsageLimit;
	}, [disabled, costPreviewRunning, activeBonusUsageLimit]);

	const mainDiscount = useMemo(() => {
		// Basic/flexible step before the cost calculation answers (plan 8, item 11).
		if (mainPlan?.stepPending && !mainPlan?.discount) return "—";
		if (!mainPlan || !mainPlan?.discount) {
			return "0";
		}
		const { unit, value } = mainPlan.discount;
		if (unit === DiscountUnit.Amount)
			return `${value}${currencyGlobalSettings}`;
		return `${value}%`;
	}, [mainPlan, currencyGlobalSettings]);

	const mainDiscountMax = useMemo(() => {
		if (!mainPlan || !mainPlan?.maxDiscount) {
			return "";
		}
		const { active, value } = mainPlan.maxDiscount;

		if (!active) return ``;
		return `<= ${value}${currencyGlobalSettings}`;
	}, [currencyGlobalSettings, mainPlan]);

	const mainDiscountActive = useMemo(
		() => mainPlan?.active,
		[mainPlan?.active],
	);

	const additionalDiscount = useMemo(() => {
		if (additionalPlan?.stepPending && !additionalPlan?.discount)
			return "—";
		if (!additionalPlan || !additionalPlan?.discount) {
			return "0";
		}
		const { unit, value } = additionalPlan.discount;
		if (unit === DiscountUnit.Amount)
			return `${value}${currencyGlobalSettings}`;
		return `${value}%`;
	}, [additionalPlan, currencyGlobalSettings]);

	const additionalDiscountMax = useMemo(() => {
		if (!additionalPlan || !additionalPlan?.maxDiscount) {
			return "";
		}

		const { active, value } = additionalPlan.maxDiscount;

		if (!active) return ``;

		return `<= ${value}${currencyGlobalSettings}`;
	}, [additionalPlan, currencyGlobalSettings]);

	const additionalDiscountActive = useMemo(
		() => additionalPlan?.active,
		[additionalPlan?.active],
	);

	return (
		<StyledRow
			alignItems="center"
			justify="start"
			p="0 12px"
			gap="0 10px"
			w="100%"
		>
			{!show && (
				<StyledSpan colors="#9BA3A8">
					{t(["discount.discountPlanClient"])}
				</StyledSpan>
			)}
			{show && (
				<>
					<StyledColumn
						justify="start"
						alignItems="start"
						gap="2px 10px"
					>
						<CheckBox
							disabled
							// disabled={
							// 	!checkFlagAccessControl[TabKeys.DISCOUNT_PLAN]
							// }
							label={t(["discount.discountPlan"]) || ""}
							value={activeDiscountUsage}
							onChange={handelDiscount}
						/>
						<StyledRow alignItems="start" gap="0 5px">
							{/* Bonus trip is hidden until the server applies it to the
							price (discount 0 now). Was:
							<CheckBox
								disabled
								label={t(["discount.bonusTrip"]) || ""}
								value={
									typeBonusUsage ===
									CustomerBonusUsageType.BonusTrip
								}
								onChange={handelBonusTripType}
							/> */}
							<CheckBox
								disabled={
									disabledBonusCheckBox ||
									!isOwn ||
									!checkFlagAccessControl[
										TabKeys.BONUS_BALANCE
									]
								}
								label={t(["discount.bonusBalance"]) || ""}
								value={
									typeBonusUsage ===
									CustomerBonusUsageType.BonusBalance
								}
								onChange={handelBonusBalanceType}
							/>
						</StyledRow>
					</StyledColumn>

					<>
						{mainDiscountActive && (
							<StyledColumn
								alignItems="start"
								justify="space-between"
								gap="2px"
							>
								<StyledP>{`${t([
									"discount.mainPlan",
								])}`}</StyledP>
								<StyledRow alignItems="center">
									<Text
										textStyle={{
											font: {
												size: "13px",
												fw: "400",
												line: "1",
											},
										}}
										valueStyle={{
											font: {
												size: "13px",
												fw: "400",
												line: "1",
											},
											textAlign: "center",
										}}
										text={mainDiscount}
										value={mainDiscountMax}
									/>
								</StyledRow>
							</StyledColumn>
						)}
						{additionalDiscountActive && (
							<StyledColumn
								alignItems="start"
								justify="space-between"
								gap="2px"
							>
								<StyledP>{`${t([
									"discount.additionalPlan",
								])}`}</StyledP>
								<StyledRow alignItems="center">
									<Text
										textStyle={{
											font: {
												size: "13px",
												fw: "400",
												line: "1",
											},
										}}
										valueStyle={{
											font: {
												size: "13px",
												fw: "400",
												line: "1",
											},
											textAlign: "center",
										}}
										text={additionalDiscount}
										value={additionalDiscountMax}
									/>
								</StyledRow>
							</StyledColumn>
						)}
					</>

					<StyledRow alignItems="center" gap="0 10px" m="0 0 0 auto">
						<StyledColumn alignItems="start" gap="2px">
							<CheckBox
								label={t(["discount.useDiscount"]) || ""}
								disabled={
									disabledBonusCheckBox ||
									!isOwn ||
									!checkFlagAccessControl[
										TabKeys.ACCUMULATIVE_DISCOUNT
									]
								}
								value={activeBonusUsage}
								onChange={handelActionBonusUsage}
							/>
							<CheckBox
								label={t(["discount.useBonus"]) || ""}
								disabled={
									disabledBonusLimitCheckBox ||
									!isOwn ||
									!checkFlagAccessControl[
										TabKeys.MAX_BONUSES_AND_INPUT
									]
								}
								value={activeBonusUsageLimit}
								onChange={handelActionBonusUsageLimit}
							/>
						</StyledColumn>
						<StyledRow
							flex={{ wrap: "nowrap" }}
							alignItems="center"
							gap="0px 5px"
						>
							<Stepper
								disabled={
									disabledBonusValue ||
									!isOwn ||
									!checkFlagAccessControl[
										TabKeys.MAX_BONUSES_AND_INPUT
									]
								}
								value={maxAmountBonusUsageLimit}
								min={0}
								onChange={handelMaxAmountBonusUsageLimit}
							/>
							{`${currencyGlobalSettings}`}
						</StyledRow>
					</StyledRow>
				</>
			)}
		</StyledRow>
	);
};

declare namespace DiscountPlaneBase {
	interface Value {
		activeDiscount: Discount["active"];
		activeDiscountUsage: Discount["discountUsage"]["active"];
		activeBonusUsage: Discount["bonusUsage"]["active"];
		typeBonusUsage: Discount["bonusUsage"]["type"];
		activeBonusUsageLimit: Discount["bonusUsage"]["limit"]["active"];
		maxAmountBonusUsageLimit: Discount["bonusUsage"]["limit"]["maxAmount"];
	}

	interface Props {
		form: UseCreateFormReturn<Schema>;
		tab: Tab;
		disabled: boolean;
	}
}

export const DiscountPlane = memo(DiscountPlaneBase);
export default DiscountPlaneBase;
